/**
 * Force Refresh Modal
 * 
 * Blocking modal shown when SuperAdmin makes company-level changes.
 * Cannot be closed - user must refresh to continue.
 */

import React, { useEffect, useState } from 'react';
import { useClient } from '../contexts/ClientContext';

// Singleton flag to prevent multiple modals
let isModalOpen = false;

const ForceRefreshModal: React.FC = () => {
  const { client } = useClient();
  const [showModal, setShowModal] = useState(false);

  // Check if user is internal role (admin/manager/agent)
  const isInternalRole = client?.role && ['admin', 'manager', 'agent'].includes(client.role);

  useEffect(() => {
    // Only listen if user is internal role
    if (!isInternalRole) return;

    const handleForceRefresh = (event: CustomEvent) => {
      // Singleton: only show if not already open
      if (isModalOpen) {
        return;
      }

      // Note: Server already filtered by company room, so if we received this event,
      // it's for our company. We can safely show the modal.
      
      isModalOpen = true;
      setShowModal(true);
    };

    // Listen for force refresh events from SocketProvider
    window.addEventListener('company:force_refresh' as any, handleForceRefresh as EventListener);

    return () => {
      window.removeEventListener('company:force_refresh' as any, handleForceRefresh as EventListener);
    };
  }, [isInternalRole]);

  // Handle refresh button click
  const handleRefresh = () => {
    window.location.reload();
  };

  // Prevent ESC key and click outside (blocking modal)
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent ESC from closing
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [showModal]);

  // Reset singleton flag when modal closes (shouldn't happen, but safety)
  useEffect(() => {
    if (!showModal && isModalOpen) {
      isModalOpen = false;
    }
  }, [showModal]);

  if (!showModal || !isInternalRole) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75 p-4" 
      dir="rtl"
      onClick={(e) => {
        // Prevent closing with click outside
        e.preventDefault();
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        // Prevent closing with ESC
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
            <svg 
              className="w-8 h-8 text-blue-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
          בוצעו שינויים על ידי חברת WJS 365ᵈ Together
        </h2>

        {/* Message */}
        <div className="text-center text-gray-700 mb-6 space-y-2">
          <p className="text-base">
            כדי להמשיך לעבוד, חובה לרענן את המערכת ולקבל את ההגדרות החדשות.
          </p>
        </div>

        {/* Button */}
        <div className="flex justify-center">
          <button
            onClick={handleRefresh}
            className="px-8 py-3 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all"
          >
            רענן עכשיו
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForceRefreshModal;

